import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { useTheme } from "next-themes";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { formatPhoneInputValue } from "@/lib/phone";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  User,
  Bell,
  Shield,
  Palette,
  Save,
  Mail,
  Phone,
  Zap,
  Loader2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { PhoneSettingsTab } from "./tabs/PhoneSettingsTab";
import { useRole, type UserRole } from "@/hooks/useRole";
import TwoFactorSettingsPanel from "@/components/TwoFactorSettingsPanel";

// Define which tabs each role can see
const ROLE_VISIBLE_TABS: Record<UserRole, string[]> = {
  superadmin: ["profile", "notifications", "security", "appearance", "email", "phone"],
  broker: ["profile", "notifications", "security", "appearance", "email", "phone"],
  employer: ["profile", "notifications", "security", "appearance"],
  employee: ["profile", "notifications", "security", "appearance"],
};

// Email settings type
interface EmailSettings {
  agencyId: string;
  agencyName?: string;
  suggestedSubdomain?: string;
  sendgridSubuserName?: string;
  sendingSubdomain?: string;
  fromEmail?: string;
  fromName?: string;
  replyToEmail?: string;
  provisioningStatus: string;
  provisioningError?: string;
  isActive: boolean;
  emailsSentThisMonth?: number;
  sendgridConfigured: boolean;
}

// Email stats type
interface EmailStats {
  emailsSentThisMonth: number;
  stats: {
    requests: number;
    delivered: number;
    opens: number;
    clicks: number;
    bounces: number;
    spam_reports: number;
  } | null;
}

export default function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const { currentRole } = useRole();

  // Get visible tabs based on role
  const visibleTabs = ROLE_VISIBLE_TABS[currentRole] || ROLE_VISIBLE_TABS.employee;

  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(["profile", "notifications", "security", "appearance", "email", "phone"]).withDefault("profile")
  );

  // Redirect to first visible tab if current tab is not allowed
  useEffect(() => {
    if (activeTab && !visibleTabs.includes(activeTab)) {
      setActiveTab((visibleTabs[0] || "profile") as typeof activeTab);
    }
  }, [activeTab, visibleTabs, setActiveTab]);

  const { data: user } = useQuery<any>({
    queryKey: ["/api/auth/user"],
  });

  const { data: profile } = useQuery<any>({
    queryKey: ["/api/user/profile"],
  });

  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // --- Change Password (Security tab) ---
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  const changePasswordMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/change-password", {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      return res.json();
    },
    onSuccess: () => {
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordErrors({});
      toast({ title: "Password updated successfully", variant: "success" });
    },
    onError: (error: any) => {
      const message = String(error?.message || "");
      // Surface a wrong current password inline on its field; anything else
      // as a toast.
      if (message.toLowerCase().includes("current password")) {
        setPasswordErrors({ currentPassword: "The current password you entered is incorrect" });
      } else {
        toast({
          title: "Failed to update password",
          description: message.replace(/^\d+:\s*/, "") || "Please try again.",
          variant: "destructive",
        });
      }
    },
  });

  const handlePasswordSubmit = () => {
    const errors: Record<string, string> = {};
    if (!passwordForm.currentPassword) errors.currentPassword = "Current password is required";
    if (!passwordForm.newPassword) errors.newPassword = "New password is required";
    else if (passwordForm.newPassword.length < 8)
      errors.newPassword = "Password must be at least 8 characters";
    if (!passwordForm.confirmPassword) errors.confirmPassword = "Please confirm your new password";
    else if (passwordForm.newPassword && passwordForm.confirmPassword !== passwordForm.newPassword)
      errors.confirmPassword = "Passwords do not match";
    setPasswordErrors(errors);
    if (Object.keys(errors).length > 0) return;
    changePasswordMutation.mutate();
  };

  // Notification toggles are persisted per (user, portalType, category) in
  // notification_preferences. Missing row => default (email/tasks ON, marketing OFF).
  const notifPortalType: "broker" | "employer" | "employee" =
    currentRole === "employer" ? "employer" : currentRole === "employee" ? "employee" : "broker";
  const notifPrefsKey = ["/api/notification-preferences", notifPortalType];

  const { data: notificationPrefs } = useQuery<any[]>({
    queryKey: notifPrefsKey,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/notification-preferences?portalType=${notifPortalType}`);
      return res.json();
    },
  });

  const prefEnabled = (category: string, fallback: boolean) =>
    notificationPrefs?.find((p) => p.category === category)?.enabled ?? fallback;
  const notifications = {
    emailNotifications: prefEnabled("email_all", true),
    taskUpdates: prefEnabled("tasks", true),
    marketingEmails: prefEnabled("marketing", false),
  };

  const savePrefMutation = useMutation({
    mutationFn: async (vars: { category: string; enabled: boolean }) => {
      const res = await apiRequest("PUT", "/api/notification-preferences", {
        portalType: notifPortalType,
        category: vars.category,
        enabled: vars.enabled,
      });
      return res.json();
    },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: notifPrefsKey });
      const prev = queryClient.getQueryData<any[]>(notifPrefsKey);
      queryClient.setQueryData<any[]>(notifPrefsKey, (old = []) => {
        if (old.some((p) => p.category === vars.category)) {
          return old.map((p) => (p.category === vars.category ? { ...p, enabled: vars.enabled } : p));
        }
        return [...old, { category: vars.category, portalType: notifPortalType, enabled: vars.enabled }];
      });
      return { prev };
    },
    onError: (error: any, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(notifPrefsKey, ctx.prev);
      toast({ title: "Couldn't save preference", description: error?.message || "Please try again.", variant: "destructive" });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: notifPrefsKey }),
  });

  // Email tab state
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);
  const [customSubdomain, setCustomSubdomain] = useState("");

  // Initialize form when profile loads
  useEffect(() => {
    if (profile) {
      setProfileForm({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        phone: profile.phone || "",
      });
    }
  }, [profile]);

  // Email settings query
  const { data: emailSettings, isLoading: emailSettingsLoading } = useQuery<EmailSettings>({
    queryKey: ["/api/agency/email-settings"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/agency/email-settings");
      return res.json();
    },
  });

  // Email stats query
  const { data: emailStats } = useQuery<EmailStats>({
    queryKey: ["/api/agency/email-settings/stats"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/agency/email-settings/stats");
      return res.json();
    },
    enabled: emailSettings?.provisioningStatus === "provisioned",
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("PATCH", "/api/user/profile", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Provision email mutation
  // Editable half of the sender identity. Seeded from the server once loaded so
  // the Save button can tell an actual edit from an untouched form.
  const [senderDraft, setSenderDraft] = useState({ fromName: "", replyToEmail: "" });
  useEffect(() => {
    if (!emailSettings) return;
    setSenderDraft({
      fromName: emailSettings.fromName ?? "",
      replyToEmail: emailSettings.replyToEmail ?? "",
    });
  }, [emailSettings?.fromName, emailSettings?.replyToEmail]);

  const senderDetailsChanged =
    senderDraft.fromName !== (emailSettings?.fromName ?? "") ||
    senderDraft.replyToEmail !== (emailSettings?.replyToEmail ?? "");

  const saveSenderMutation = useMutation({
    mutationFn: async (draft: { fromName: string; replyToEmail: string }) => {
      const res = await apiRequest("PUT", "/api/agency/email-settings", draft);
      const body = await res.json();
      if (!res.ok) throw new Error(body?.message ?? "Failed to save sender details");
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/email-settings"] });
      toast({ title: "Sender details saved" });
    },
    onError: (error: any) =>
      toast({ title: "Couldn't save", description: error.message, variant: "destructive" }),
  });

  const provisionEmailMutation = useMutation({
    mutationFn: async (customSubdomain?: string) => {
      const res = await apiRequest("POST", "/api/agency/email-settings/provision", {
        customSubdomain,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/email-settings"] });
      setProvisionModalOpen(false);
      setCustomSubdomain("");
      toast({
        title: "Email Service Enabled",
        description: "Your email sending has been set up successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to enable email service",
        variant: "destructive",
      });
    },
  });

  // Test email mutation
  const testEmailMutation = useMutation({
    mutationFn: async (toEmail: string) => {
      const res = await apiRequest("POST", "/api/agency/email-settings/test-email", {
        toEmail,
      });
      return res.json();
    },
    onSuccess: () => {
      setTestEmailAddress("");
      toast({
        title: "Test Email Sent",
        description: "Check your inbox for the test email.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send test email",
        variant: "destructive",
      });
    },
  });

  const handleSaveProfile = () => {
    updateProfileMutation.mutate(profileForm);
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return "U";
  };

  const handleProvisionEmail = () => {
    provisionEmailMutation.mutate(customSubdomain || undefined);
  };

  const handleSendTestEmail = () => {
    if (testEmailAddress) {
      testEmailMutation.mutate(testEmailAddress);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings and preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
        <TabsList className="bg-[#eff6fe] dark:bg-gray-800">
          {visibleTabs.includes("profile") && (
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:text-gray-300 dark:data-[state=active]:bg-blue-600">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
          )}
          {visibleTabs.includes("notifications") && (
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:text-gray-300 dark:data-[state=active]:bg-blue-600">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
          )}
          {visibleTabs.includes("security") && (
            <TabsTrigger value="security" className="data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:text-gray-300 dark:data-[state=active]:bg-blue-600">
              <Shield className="h-4 w-4 mr-2" />
              Security
            </TabsTrigger>
          )}
          {visibleTabs.includes("appearance") && (
            <TabsTrigger value="appearance" className="data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:text-gray-300 dark:data-[state=active]:bg-blue-600">
              <Palette className="h-4 w-4 mr-2" />
              Appearance
            </TabsTrigger>
          )}
          {visibleTabs.includes("email") && (
            <TabsTrigger value="email" className="data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:text-gray-300 dark:data-[state=active]:bg-blue-600">
              <Mail className="h-4 w-4 mr-2" />
              Email
            </TabsTrigger>
          )}
          {visibleTabs.includes("phone") && (
            <TabsTrigger value="phone" className="data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:text-gray-300 dark:data-[state=active]:bg-blue-600">
              <Phone className="h-4 w-4 mr-2" />
              Phone & SMS
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={profile?.profilePictureUrl} />
                  <AvatarFallback className="text-lg bg-[#1c35a7] text-white">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-medium">{user?.firstName} {user?.lastName}</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={profileForm.firstName || profile?.firstName || ""}
                    disabled={currentRole === "employee"}
                    onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={profileForm.lastName || profile?.lastName || ""}
                    disabled={currentRole === "employee"}
                    onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile?.email || ""}
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-muted-foreground">
                    Your login email can't be changed here — contact your administrator.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="(555) 123-4567"
                    value={profileForm.phone || profile?.phone || ""}
                    disabled={currentRole === "employee"}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: formatPhoneInputValue(e.target.value) })}
                  />
                </div>
              </div>

              {currentRole === "employee" ? (
                <p className="text-sm text-muted-foreground">
                  Your name and phone are managed by your employer. Contact them to make changes.
                </p>
              ) : (
                <Button onClick={handleSaveProfile} disabled={updateProfileMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Manage how you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">Receive email notifications for important updates</p>
                </div>
                <Switch
                  checked={notifications.emailNotifications}
                  onCheckedChange={(checked) => savePrefMutation.mutate({ category: "email_all", enabled: checked })}
                />
              </div>
              {/* Tasks only for roles that are task assignees */}
              {["broker", "agency_member", "superadmin"].includes(currentRole) && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Task Updates</p>
                      <p className="text-sm text-muted-foreground">Notifications when tasks are assigned or updated</p>
                    </div>
                    <Switch
                      checked={notifications.taskUpdates}
                      onCheckedChange={(checked) => savePrefMutation.mutate({ category: "tasks", enabled: checked })}
                    />
                  </div>
                </>
              )}
              {/* Marketing newsletters don't go to employees */}
              {currentRole !== "employee" && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Marketing Emails</p>
                      <p className="text-sm text-muted-foreground">Receive product updates and newsletters</p>
                    </div>
                    <Switch
                      checked={notifications.marketingEmails}
                      onCheckedChange={(checked) => savePrefMutation.mutate({ category: "marketing", enabled: checked })}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium">Change Password</h3>
                  <p className="text-sm text-muted-foreground mb-4">Update your password to keep your account secure</p>
                  <form
                    className="space-y-4 max-w-md"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handlePasswordSubmit();
                    }}
                  >
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">
                        Current Password <span className="text-red-500">*</span>
                      </Label>
                      <PasswordInput
                        id="currentPassword"
                        autoComplete="current-password"
                        value={passwordForm.currentPassword}
                        className={passwordErrors.currentPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
                        onChange={(e) => {
                          setPasswordForm((f) => ({ ...f, currentPassword: e.target.value }));
                          setPasswordErrors(({ currentPassword, ...rest }) => rest);
                        }}
                      />
                      {passwordErrors.currentPassword && (
                        <p className="text-xs text-red-600">{passwordErrors.currentPassword}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">
                        New Password <span className="text-red-500">*</span>
                      </Label>
                      <PasswordInput
                        id="newPassword"
                        autoComplete="new-password"
                        value={passwordForm.newPassword}
                        className={passwordErrors.newPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
                        onChange={(e) => {
                          setPasswordForm((f) => ({ ...f, newPassword: e.target.value }));
                          setPasswordErrors(({ newPassword, ...rest }) => rest);
                        }}
                      />
                      {passwordErrors.newPassword && (
                        <p className="text-xs text-red-600">{passwordErrors.newPassword}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">
                        Confirm New Password <span className="text-red-500">*</span>
                      </Label>
                      <PasswordInput
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={passwordForm.confirmPassword}
                        className={passwordErrors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
                        onChange={(e) => {
                          setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }));
                          setPasswordErrors(({ confirmPassword, ...rest }) => rest);
                        }}
                      />
                      {passwordErrors.confirmPassword && (
                        <p className="text-xs text-red-600">{passwordErrors.confirmPassword}</p>
                      )}
                    </div>
                    <Button type="submit" disabled={changePasswordMutation.isPending}>
                      {changePasswordMutation.isPending ? "Updating…" : "Update Password"}
                    </Button>
                  </form>
                </div>
                <Separator />
                <TwoFactorSettingsPanel />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize how the application looks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-medium mb-4">Theme</h3>
                <div className="flex gap-4">
                  <Button
                    variant={theme === "light" ? "default" : "outline"}
                    className="flex-1 h-20 flex-col"
                    onClick={() => setTheme("light")}
                  >
                    <div className="w-8 h-8 rounded bg-white border mb-2" />
                    Light
                  </Button>
                  <Button
                    variant={theme === "dark" ? "default" : "outline"}
                    className="flex-1 h-20 flex-col"
                    onClick={() => setTheme("dark")}
                  >
                    <div className="w-8 h-8 rounded bg-gray-900 mb-2" />
                    Dark
                  </Button>
                  <Button
                    variant={theme === "system" ? "default" : "outline"}
                    className="flex-1 h-20 flex-col"
                    onClick={() => setTheme("system")}
                  >
                    <div className="w-8 h-8 rounded bg-gradient-to-r from-white to-gray-900 mb-2" />
                    System
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email" className="space-y-6">
          {emailSettingsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !emailSettings?.sendgridConfigured ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mx-auto mb-4">
                    <Mail className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Email Service Not Available</h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    Email sending requires SendGrid configuration. Please contact support to enable this feature.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : emailSettings?.provisioningStatus !== "provisioned" ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4">
                    <Mail className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Enable Email Sending</h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
                    Set up your agency's email sending to send marketing emails with your own subdomain.
                  </p>
                  {emailSettings?.provisioningError && (
                    <div className="flex items-center gap-2 text-red-600 mb-4 justify-center">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">{emailSettings.provisioningError}</span>
                    </div>
                  )}
                  <Dialog open={provisionModalOpen} onOpenChange={setProvisionModalOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <Zap className="h-4 w-4 mr-2" />
                        Enable Email Service
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Enable Email Service</DialogTitle>
                        <DialogDescription>
                          Set up your agency's email sending subdomain. This will be used for sending marketing emails.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Suggested Subdomain</Label>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="font-mono">
                              noreply+{emailSettings?.suggestedSubdomain || "your-agency"}@administrmail.com
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="customSubdomain">Custom Subdomain (optional)</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="customSubdomain"
                              value={customSubdomain}
                              onChange={(e) => setCustomSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                              placeholder={emailSettings?.suggestedSubdomain || "your-agency"}
                              className="font-mono"
                            />
                            <span className="text-sm text-muted-foreground">@administrmail.com</span>
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setProvisionModalOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleProvisionEmail} disabled={provisionEmailMutation.isPending}>
                          {provisionEmailMutation.isPending ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Setting up...
                            </>
                          ) : (
                            "Enable"
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Email Configuration</CardTitle>
                      <CardDescription>Your agency's email sending settings</CardDescription>
                    </div>
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Fixed by provisioning — both derive from the verified
                        sending domain and can't be edited here. */}
                    <div>
                      <Label className="text-muted-foreground">Sending Domain</Label>
                      <p className="font-mono text-sm">{emailSettings?.sendingSubdomain}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">From Email</Label>
                      <p className="font-mono text-sm">{emailSettings?.fromEmail}</p>
                    </div>
                    <div>
                      <Label htmlFor="from-name">From Name</Label>
                      <Input
                        id="from-name"
                        className="mt-1"
                        placeholder="Your agency's name"
                        value={senderDraft.fromName}
                        onChange={(e) => setSenderDraft((d) => ({ ...d, fromName: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">Shown as the sender name.</p>
                    </div>
                    <div>
                      <Label htmlFor="reply-to">Reply-To</Label>
                      <Input
                        id="reply-to"
                        className="mt-1"
                        placeholder="you@youragency.com"
                        value={senderDraft.replyToEmail}
                        onChange={(e) => setSenderDraft((d) => ({ ...d, replyToEmail: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Where replies land. The From address is a no-reply mailbox, so without this
                        an answer to your email goes nowhere.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      disabled={!senderDetailsChanged || saveSenderMutation.isPending}
                      onClick={() => saveSenderMutation.mutate(senderDraft)}
                    >
                      {saveSenderMutation.isPending ? "Saving..." : "Save sender details"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {emailStats && (
                <Card>
                  <CardHeader>
                    <CardTitle>Email Statistics</CardTitle>
                    <CardDescription>This month's email sending stats</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 rounded-lg bg-muted">
                        <p className="text-2xl font-bold">{emailStats.emailsSentThisMonth}</p>
                        <p className="text-sm text-muted-foreground">Sent</p>
                      </div>
                      <div className="text-center p-4 rounded-lg bg-muted">
                        <p className="text-2xl font-bold">{emailStats.stats?.delivered || 0}</p>
                        <p className="text-sm text-muted-foreground">Delivered</p>
                      </div>
                      <div className="text-center p-4 rounded-lg bg-muted">
                        <p className="text-2xl font-bold">{emailStats.stats?.opens || 0}</p>
                        <p className="text-sm text-muted-foreground">Opens</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Send Test Email</CardTitle>
                  <CardDescription>Verify your email configuration is working</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      className="max-w-sm"
                    />
                    <Button
                      onClick={handleSendTestEmail}
                      disabled={!testEmailAddress || testEmailMutation.isPending}
                    >
                      {testEmailMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Send Test
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="phone" className="space-y-6">
          <PhoneSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
